// Cut To Length Optimiser — branded A4 PDF export via jspdf (lazy-loaded).
// Ported from the Drillcut Tools Hub exporter, restyled with the BSP
// palette (brand blue #1B449C, Barlow-adjacent helvetica) and the BSP logo.

import {
  type Mode,
  type MultiProfileResults,
  type Results,
  colorFor,
  consolidatePatterns,
  fmtDateAU,
  lightColorFor,
} from "./ctlOptimiser";

// BSP palette as RGB tuples for jspdf
const BLUE: [number, number, number] = [27, 68, 156]; // --bsp-blue #1B449C
const BLUE_PALE: [number, number, number] = [242, 245, 251]; // --bsp-blue-pale
const BLUE_BORDER: [number, number, number] = [214, 222, 238]; // --bsp-blue-border
const ROW_ALT: [number, number, number] = [248, 249, 253];
const KERF_RGB: [number, number, number] = [185, 201, 230];
const FG: [number, number, number] = [35, 40, 48]; // --bsp-fg #232830
const FG_MUTED: [number, number, number] = [112, 115, 114]; // --bsp-fg-muted
const WASTE_LINE: [number, number, number] = [200, 202, 202]; // --bsp-border-strong

export interface CtlPdfContext {
  mode: Mode;
  results: Results | null;
  multiResults: MultiProfileResults | null;
  projectNumber: string;
  projectName: string;
  jobName: string;
  projectAddress: string;
  client: string;
  notes: string;
  jobRef: string;
  jobDate: string;
  revision: string;
  createdBy: string;
  multiplier: string;
}

let _cachedLogo: { dataUrl: string; ratio: number } | null | undefined;

/** Load the BSP logo as a data URL (cached). Returns null if unavailable. */
async function loadBspLogo(): Promise<{ dataUrl: string; ratio: number } | null> {
  if (_cachedLogo !== undefined) return _cachedLogo;
  try {
    const res = await fetch("/assets/bsp-logo.png");
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    const ratio = await new Promise<number>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth / img.naturalHeight);
      img.onerror = () => reject(new Error("logo decode failed"));
      img.src = dataUrl;
    });
    _cachedLogo = { dataUrl, ratio };
  } catch {
    _cachedLogo = null;
  }
  return _cachedLogo;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

export async function exportCtlPdf(ctx: CtlPdfContext): Promise<void> {
  const {
    mode,
    results,
    multiResults,
    projectNumber,
    projectName,
    jobName,
    projectAddress,
    client,
    notes,
    jobRef,
    jobDate,
    revision,
    createdBy,
    multiplier,
  } = ctx;

  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const footerZone = 14;
  const maxY = pageHeight - footerZone;
  let y = margin;
  let pageNum = 1;

  const docNumber = projectNumber
    ? `${projectNumber}-CL${revision ? `-${revision}` : ""}`
    : "";

  const addFooter = () => {
    const fy = pageHeight - 8;
    pdf.setDrawColor(...BLUE);
    pdf.setLineWidth(0.5);
    pdf.line(margin, fy - 3, pageWidth - margin, fy - 3);
    pdf.setTextColor(...BLUE);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "bold");
    pdf.text("BSP Engineering — Cut To Length Optimiser", margin, fy);
    pdf.setTextColor(...FG_MUTED);
    pdf.setFont("helvetica", "normal");
    const parts = [docNumber || jobName, jobRef, fmtDateAU(jobDate)]
      .filter(Boolean)
      .join(" · ");
    pdf.text(parts || fmtDateAU(jobDate), pageWidth - margin - 20, fy, {
      align: "right",
    });
    pdf.text(`Page ${pageNum}`, pageWidth - margin, fy, { align: "right" });
  };

  const ensureSpace = (needed: number) => {
    if (y + needed > maxY) {
      addFooter();
      pdf.addPage();
      pageNum++;
      y = margin;
    }
  };

  // ── Header bar ──
  pdf.setFillColor(...BLUE);
  pdf.rect(0, 0, pageWidth, 22, "F");

  const logo = await loadBspLogo();
  if (logo) {
    try {
      const logoH = 10;
      const logoW = logoH * logo.ratio;
      // White chip behind the logo so the artwork reads on the blue band
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin - 2, 4.5, logoW + 4, logoH + 3, 1.5, 1.5, "F");
      pdf.addImage(logo.dataUrl, "PNG", margin, 6, logoW, logoH);
    } catch {
      /* logo optional */
    }
  }

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "bold");
  if (docNumber) {
    pdf.text(docNumber, pageWidth - margin, 10, { align: "right" });
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.text("Cut To Length Optimiser", pageWidth - margin, 16, {
      align: "right",
    });
  } else {
    pdf.text("Cut To Length Optimiser", pageWidth - margin, 10, {
      align: "right",
    });
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.text("Cutting yield optimiser · kerf-aware", pageWidth - margin, 16, {
      align: "right",
    });
  }

  y = 30;

  // ── Project / Job details ──
  pdf.setTextColor(...BLUE);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.text("PROJECT DETAILS", margin, y);
  y += 5;
  pdf.setTextColor(...FG);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");

  const detailLines: string[] = [];
  if (projectNumber) detailLines.push(`Project No: ${projectNumber}`);
  if (projectName) detailLines.push(`Project: ${projectName}`);
  if (jobName) detailLines.push(`Job: ${jobName}`);
  if (client) detailLines.push(`Client: ${client}`);
  if (projectAddress) detailLines.push(`Address: ${projectAddress}`);
  if (jobRef) detailLines.push(`Drawing / Ref: ${jobRef}`);
  detailLines.push(`Date: ${fmtDateAU(jobDate)}`);
  if (revision) detailLines.push(`Revision: ${revision}`);
  if (createdBy) detailLines.push(`Created By: ${createdBy}`);

  for (const line of detailLines) {
    pdf.text(line, margin, y);
    y += 4;
  }

  if (notes) {
    pdf.text(`Notes: ${notes}`, margin, y);
    y += 4;
  }

  // For standard mode, add profile/material info
  if (mode === "standard" && results) {
    if (results.profileCode) {
      pdf.text(`Profile Code: ${results.profileCode}`, margin, y);
      y += 4;
    }
    if (results.matDesc) {
      pdf.text(`Material: ${results.matDesc}`, margin, y);
      y += 4;
    }
    pdf.text(
      `Stock Length: ${results.stockLen}mm · Kerf: ${results.kerf}mm`,
      margin,
      y
    );
    y += 4;
  }

  // Multiplier note
  const mult = parseInt(multiplier) || 1;
  if (mult > 1) {
    pdf.setTextColor(...BLUE);
    pdf.setFont("helvetica", "bold");
    pdf.text(`Multiplier: ×${mult} (applied to all quantities)`, margin, y);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...FG);
    y += 4;
  }

  y += 4;

  // ── STANDARD MODE PDF ──
  if (mode === "standard" && results) {
    // Summary metrics
    pdf.setFillColor(...BLUE_PALE);
    pdf.roundedRect(margin, y, contentWidth, 18, 2, 2, "F");
    pdf.setTextColor(...BLUE);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "bold");
    const metrics = [
      `Lengths: ${results.stocks.length}`,
      `Yield: ${results.yieldPct.toFixed(1)}%`,
      `Waste: ${results.totalWaste.toFixed(0)}mm`,
      `Kerf Loss: ${results.totalKerfs.toFixed(0)}mm`,
      `Total Req: ${(results.totalStock / 1000).toFixed(2)}m`,
      `Pieces: ${results.stocks.reduce((a, s) => a + s.cuts.length, 0)}`,
    ];
    const colW = contentWidth / metrics.length;
    metrics.forEach((m, i) => {
      pdf.text(m, margin + colW * i + colW / 2, y + 11, { align: "center" });
    });
    y += 24;

    // Cut schedule table
    pdf.setTextColor(...BLUE);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("CUT SCHEDULE", margin, y);
    y += 5;

    const rowH = 6;
    pdf.setFillColor(...BLUE);
    pdf.rect(margin, y, contentWidth, rowH, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.text("Size", margin + 2, y + 4.2);
    pdf.text("Qty", margin + contentWidth * 0.6, y + 4.2);
    pdf.text("Total mm", margin + contentWidth * 0.8, y + 4.2);
    y += rowH + 3;

    pdf.setTextColor(...FG);
    pdf.setFont("helvetica", "normal");
    for (let ri = 0; ri < results.cutDefs.length; ri++) {
      const d = results.cutDefs[ri]!;
      ensureSpace(6);
      if (ri % 2 === 0) {
        pdf.setFillColor(...ROW_ALT);
        pdf.rect(margin, y - 3.5, contentWidth, 5.5, "F");
      }
      pdf.setTextColor(...FG);
      pdf.text(
        `${d.len}mm${results.matDesc ? ` — ${results.matDesc}` : ""}`,
        margin + 2,
        y
      );
      pdf.text(String(d.qty), margin + contentWidth * 0.6, y);
      pdf.text(String(d.len * d.qty), margin + contentWidth * 0.8, y);
      y += 5.5;
    }
    pdf.setFillColor(...BLUE_PALE);
    pdf.rect(margin, y - 3, contentWidth, 6, "F");
    pdf.setTextColor(...BLUE);
    pdf.setFont("helvetica", "bold");
    pdf.text("TOTAL", margin + 2, y);
    pdf.text(
      String(results.stocks.reduce((a, s) => a + s.cuts.length, 0)),
      margin + contentWidth * 0.6,
      y
    );
    pdf.text(String(results.totalPiece), margin + contentWidth * 0.8, y);
    y += 10;

    // Bar layouts
    const consolidatedStocks = consolidatePatterns(results.stocks);

    ensureSpace(20);
    pdf.setTextColor(...BLUE);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("LENGTH LAYOUT — OPTIMISED CUT SEQUENCE", margin, y);
    y += 6;

    const barH = 8;
    const barItemHeight = 18;
    const uLens = Array.from(new Set(results.cutDefs.map(d => d.len)));

    consolidatedStocks.forEach((group, gi) => {
      ensureSpace(barItemHeight);
      const s = group.stock;
      const usedPieces = s.cuts.reduce((a, c) => a + c.len, 0);
      const wasteLen =
        results.stockLen - usedPieces - s.cuts.length * results.kerf;
      const wastePct = (wasteLen / results.stockLen) * 100;

      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...BLUE);
      const qtyLabel = group.count > 1 ? ` (x${group.count} lengths)` : "";
      pdf.text(`PATTERN ${gi + 1}${qtyLabel}`, margin, y);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...FG_MUTED);
      pdf.text(
        `${s.cuts.length} pcs · ${wasteLen.toFixed(0)}mm waste (${wastePct.toFixed(1)}%)`,
        pageWidth - margin,
        y,
        { align: "right" }
      );
      y += 3;

      pdf.setFillColor(...BLUE_PALE);
      pdf.setDrawColor(...BLUE_BORDER);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(margin, y, contentWidth, barH, 1, 1, "FD");

      let xPos = margin;
      s.cuts.forEach(c => {
        const segW = (c.len / results.stockLen) * contentWidth;
        const kerfW =
          results.kerf > 0
            ? (results.kerf / results.stockLen) * contentWidth
            : 0;

        const lightRgb = hexToRgb(lightColorFor(c.len, uLens));
        pdf.setFillColor(lightRgb[0], lightRgb[1], lightRgb[2]);
        pdf.rect(xPos, y, segW, barH, "F");

        const darkRgb = hexToRgb(colorFor(c.len, uLens));
        pdf.setDrawColor(darkRgb[0], darkRgb[1], darkRgb[2]);
        pdf.setLineWidth(0.5);
        pdf.line(xPos + segW, y, xPos + segW, y + barH);

        const labelStr = String(c.len);
        pdf.setFontSize(6);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(darkRgb[0], darkRgb[1], darkRgb[2]);
        const labelW = pdf.getTextWidth(labelStr);
        if (segW > labelW + 2) {
          pdf.text(labelStr, xPos + segW / 2, y + barH / 2 + 1, {
            align: "center",
          });
        } else if (segW > 2.5 && barH > 4) {
          const textCenterX = xPos + segW / 2;
          const textCenterY = y + barH / 2;
          pdf.text(labelStr, textCenterX + 1, textCenterY + labelW / 2, {
            angle: 90,
          });
        }

        xPos += segW;
        if (results.kerf > 0 && kerfW > 0.3) {
          pdf.setFillColor(...KERF_RGB);
          pdf.rect(xPos, y, kerfW, barH, "F");
          xPos += kerfW;
        }
      });

      if (wastePct > 0.2) {
        const wasteW = (wasteLen / results.stockLen) * contentWidth;
        const wasteX = margin + contentWidth - wasteW;
        pdf.setDrawColor(...WASTE_LINE);
        pdf.setLineWidth(0.2);
        for (let lx = wasteX; lx < margin + contentWidth; lx += 1.5) {
          pdf.line(lx, y + barH, Math.min(lx + barH, margin + contentWidth), y);
        }
        if (wastePct > 5) {
          pdf.setFontSize(5);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(150, 150, 150);
          pdf.text(
            `${Math.round(wasteLen)}mm`,
            wasteX + wasteW / 2,
            y + barH / 2 + 1,
            { align: "center" }
          );
        }
      }

      y += barH + 5;
    });

    // Legend
    const legendRowH = 5;
    const maxLegendX = pageWidth - margin;
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");

    const legendItems: {
      label: string;
      color: [number, number, number];
      hatch?: boolean;
    }[] = [];
    uLens.forEach(l => {
      legendItems.push({ label: `${l}mm`, color: hexToRgb(colorFor(l, uLens)) });
    });
    if (results.kerf > 0) {
      legendItems.push({ label: `Kerf (${results.kerf}mm)`, color: KERF_RGB });
    }
    legendItems.push({ label: "Waste", color: WASTE_LINE, hatch: true });

    const swatchW = 3;
    const swatchGap = 1;
    const itemGap = 4;
    let tempX = margin;
    let legendRows = 1;
    legendItems.forEach(item => {
      const itemW =
        swatchW + swatchGap + pdf.getTextWidth(item.label) + itemGap;
      if (tempX + itemW > maxLegendX && tempX > margin) {
        legendRows++;
        tempX = margin;
      }
      tempX += itemW;
    });

    ensureSpace(legendRows * legendRowH + 4);
    y += 2;
    let legendX = margin;

    legendItems.forEach(item => {
      const itemW =
        swatchW + swatchGap + pdf.getTextWidth(item.label) + itemGap;
      if (legendX + itemW > maxLegendX && legendX > margin) {
        legendX = margin;
        y += legendRowH;
      }
      pdf.setFillColor(item.color[0], item.color[1], item.color[2]);
      pdf.rect(legendX, y - 2, swatchW, swatchW, "F");
      if (item.hatch) {
        pdf.setDrawColor(160, 162, 162);
        pdf.setLineWidth(0.2);
        pdf.line(legendX, y - 2, legendX + swatchW, y + 1);
        pdf.line(legendX + 1.5, y - 2, legendX + swatchW, y - 0.5);
      }
      legendX += swatchW + swatchGap;
      pdf.setTextColor(...FG_MUTED);
      pdf.text(item.label, legendX, y);
      legendX += pdf.getTextWidth(item.label) + itemGap;
    });
  }

  // ── MULTI-PROFILE MODE PDF ──
  if (mode === "multi" && multiResults) {
    const multApplied = multiResults.multiplier;

    // Per-section cut schedules and bar layouts
    for (let si = 0; si < multiResults.sections.length; si++) {
      const sec = multiResults.sections[si]!;

      // Pre-calculate estimated section height to avoid mid-section page breaks
      const consolidatedPatternCount = consolidatePatterns(sec.stocks).length;
      const estSectionHeight =
        12 +
        18 +
        4 +
        7.5 +
        5 * sec.cutDefs.length +
        4 +
        4 +
        16 * consolidatedPatternCount +
        6;
      const pageContentHeight = maxY - margin;

      // If the section fits on a single page but would split at current position, start a new page
      if (estSectionHeight <= pageContentHeight && y + estSectionHeight > maxY) {
        addFooter();
        pdf.addPage();
        pageNum++;
        y = margin;
      }

      // Section header
      ensureSpace(20);
      pdf.setFillColor(...BLUE);
      pdf.rect(margin, y, contentWidth, 8, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.text(`${sec.profileName} (${sec.profileCode})`, margin + 3, y + 5.5);
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        `Stock: ${sec.stockLen}mm · Kerf: ${sec.kerf}mm`,
        pageWidth - margin - 2,
        y + 5.5,
        { align: "right" }
      );
      y += 12;

      // Section summary metrics
      pdf.setFillColor(...BLUE_PALE);
      pdf.roundedRect(margin, y, contentWidth, 14, 2, 2, "F");
      pdf.setTextColor(...BLUE);
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      const secMetrics = [
        `Lengths: ${sec.stocks.length}`,
        `Yield: ${sec.yieldPct.toFixed(1)}%`,
        `Waste: ${sec.totalWaste.toFixed(0)}mm`,
        `Pieces: ${sec.stocks.reduce((a, s) => a + s.cuts.length, 0)}`,
      ];
      const secColW = contentWidth / secMetrics.length;
      secMetrics.forEach((m, i) => {
        pdf.text(m, margin + secColW * i + secColW / 2, y + 9, {
          align: "center",
        });
      });
      y += 18;

      // Cut schedule for this section
      pdf.setTextColor(...BLUE);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.text("CUT SCHEDULE", margin, y);
      y += 4;

      const rowH = 5.5;
      pdf.setFillColor(...BLUE);
      pdf.rect(margin, y, contentWidth, rowH, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(7);
      pdf.text("Size (mm)", margin + 2, y + 3.8);
      pdf.text("Qty", margin + contentWidth * 0.6, y + 3.8);
      pdf.text("Total mm", margin + contentWidth * 0.8, y + 3.8);
      y += rowH + 2;

      pdf.setTextColor(...FG);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      for (let ri = 0; ri < sec.cutDefs.length; ri++) {
        const d = sec.cutDefs[ri]!;
        ensureSpace(5.5);
        if (ri % 2 === 0) {
          pdf.setFillColor(...ROW_ALT);
          pdf.rect(margin, y - 3, contentWidth, 5, "F");
        }
        pdf.setTextColor(...FG);
        pdf.text(`${d.len}mm`, margin + 2, y);
        pdf.text(String(d.qty), margin + contentWidth * 0.6, y);
        pdf.text(String(d.len * d.qty), margin + contentWidth * 0.8, y);
        y += 5;
      }
      y += 4;

      // Bar layouts for this section
      const consolidatedStocks = consolidatePatterns(sec.stocks);

      ensureSpace(16);
      pdf.setTextColor(...BLUE);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.text("LENGTH LAYOUT", margin, y);
      y += 4;

      const barH = 7;
      const barItemHeight = 16;
      const uLens = Array.from(new Set(sec.cutDefs.map(d => d.len)));

      consolidatedStocks.forEach((group, gi) => {
        ensureSpace(barItemHeight);
        const s = group.stock;
        const usedPieces = s.cuts.reduce((a, c) => a + c.len, 0);
        const wasteLen = sec.stockLen - usedPieces - s.cuts.length * sec.kerf;
        const wastePct = (wasteLen / sec.stockLen) * 100;

        pdf.setFontSize(7);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...BLUE);
        const qtyLabel = group.count > 1 ? ` (x${group.count})` : "";
        pdf.text(`PATTERN ${gi + 1}${qtyLabel}`, margin, y);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...FG_MUTED);
        pdf.text(
          `${wasteLen.toFixed(0)}mm waste (${wastePct.toFixed(1)}%)`,
          pageWidth - margin,
          y,
          { align: "right" }
        );
        y += 3;

        pdf.setFillColor(...BLUE_PALE);
        pdf.setDrawColor(...BLUE_BORDER);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(margin, y, contentWidth, barH, 1, 1, "FD");

        let xPos = margin;
        s.cuts.forEach(c => {
          const segW = (c.len / sec.stockLen) * contentWidth;
          const kerfW =
            sec.kerf > 0 ? (sec.kerf / sec.stockLen) * contentWidth : 0;

          const lightRgb = hexToRgb(lightColorFor(c.len, uLens));
          pdf.setFillColor(lightRgb[0], lightRgb[1], lightRgb[2]);
          pdf.rect(xPos, y, segW, barH, "F");

          const darkRgb = hexToRgb(colorFor(c.len, uLens));
          pdf.setDrawColor(darkRgb[0], darkRgb[1], darkRgb[2]);
          pdf.setLineWidth(0.5);
          pdf.line(xPos + segW, y, xPos + segW, y + barH);

          const labelStr = String(c.len);
          pdf.setFontSize(5.5);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(darkRgb[0], darkRgb[1], darkRgb[2]);
          const labelW = pdf.getTextWidth(labelStr);
          if (segW > labelW + 2) {
            pdf.text(labelStr, xPos + segW / 2, y + barH / 2 + 1, {
              align: "center",
            });
          } else if (segW > 2.5 && barH > 4) {
            const textCenterX = xPos + segW / 2;
            const textCenterY = y + barH / 2;
            pdf.text(labelStr, textCenterX + 1, textCenterY + labelW / 2, {
              angle: 90,
            });
          }

          xPos += segW;
          if (sec.kerf > 0 && kerfW > 0.3) {
            pdf.setFillColor(...KERF_RGB);
            pdf.rect(xPos, y, kerfW, barH, "F");
            xPos += kerfW;
          }
        });

        if (wastePct > 0.2) {
          const wasteW = (wasteLen / sec.stockLen) * contentWidth;
          const wasteX = margin + contentWidth - wasteW;
          pdf.setDrawColor(...WASTE_LINE);
          pdf.setLineWidth(0.2);
          for (let lx = wasteX; lx < margin + contentWidth; lx += 1.5) {
            pdf.line(
              lx,
              y + barH,
              Math.min(lx + barH, margin + contentWidth),
              y
            );
          }
          if (wastePct > 5) {
            pdf.setFontSize(5);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(150, 150, 150);
            pdf.text(
              `${Math.round(wasteLen)}mm`,
              wasteX + wasteW / 2,
              y + barH / 2 + 1,
              { align: "center" }
            );
          }
        }

        y += barH + 4;
      });

      y += 6;
    }

    // ── Material Requirements Summary ──
    ensureSpace(30);
    pdf.setTextColor(...BLUE);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("MATERIAL REQUIREMENTS SUMMARY", margin, y);
    if (multApplied > 1) {
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...FG_MUTED);
      pdf.text(
        `×${multApplied} multiplier applied to all quantities`,
        pageWidth - margin,
        y,
        { align: "right" }
      );
    }
    y += 5;

    // Table header
    const summaryHeaders = [
      "Section",
      "No. Lengths",
      "Total Waste (mm)",
      "Avg Waste/Length",
      "Avg Util.",
    ];
    const summaryColWidths = [
      contentWidth * 0.35,
      contentWidth * 0.15,
      contentWidth * 0.18,
      contentWidth * 0.18,
      contentWidth * 0.14,
    ];

    const sumRowH = 5.5;
    pdf.setFillColor(...BLUE);
    pdf.rect(margin, y, contentWidth, sumRowH, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    let sumX = margin + 2;
    summaryHeaders.forEach((h, i) => {
      pdf.text(h, sumX, y + 3.8);
      sumX += summaryColWidths[i]!;
    });
    y += sumRowH + 2;

    // Data rows
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    multiResults.sections.forEach((sec, ri) => {
      ensureSpace(5.5);
      if (ri % 2 === 0) {
        pdf.setFillColor(...ROW_ALT);
        pdf.rect(margin, y - 3, contentWidth, 5, "F");
      }
      pdf.setTextColor(...FG);
      let rx = margin + 2;
      const avgWaste =
        sec.stocks.length > 0 ? sec.totalWaste / sec.stocks.length : 0;

      const vals = [
        sec.profileName.length > 30
          ? sec.profileName.substring(0, 28) + "..."
          : sec.profileName,
        String(sec.stocks.length),
        sec.totalWaste.toFixed(0),
        avgWaste.toFixed(0),
        `${sec.yieldPct.toFixed(1)}%`,
      ];
      vals.forEach((v, i) => {
        pdf.text(v, rx, y);
        rx += summaryColWidths[i]!;
      });
      y += 5;
    });

    // Totals row
    ensureSpace(6);
    pdf.setFillColor(...BLUE_PALE);
    pdf.rect(margin, y - 3, contentWidth, 6, "F");
    pdf.setTextColor(...BLUE);
    pdf.setFont("helvetica", "bold");
    let totX = margin + 2;
    const grandLengths = multiResults.grandTotalLengths;
    const grandWaste = multiResults.grandTotalWaste;
    const grandUtil = multiResults.grandAvgUtilisation;

    const avgW = grandLengths > 0 ? grandWaste / grandLengths : 0;
    const totVals = [
      "TOTAL",
      String(grandLengths),
      grandWaste.toFixed(0),
      avgW.toFixed(0),
      `${grandUtil.toFixed(1)}%`,
    ];
    totVals.forEach((v, i) => {
      pdf.text(v, totX, y);
      totX += summaryColWidths[i]!;
    });
  }

  // ── Footers ──
  const totalPages = pdf.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    pageNum = p;
    addFooter();
  }

  const filename = [projectNumber || jobName || "CTL", jobRef, jobDate]
    .filter(Boolean)
    .join("_")
    .replace(/[^a-zA-Z0-9_-]/g, "_");
  pdf.save(`${filename}.pdf`);
}
